import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [pw, setPw] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            await api.post("/auth/reset-password", { token, new_password: pw });
            toast.success("Password updated — please sign in");
            navigate("/login");
        } catch (err) {
            const status = err.response?.status;
            const detail = err.response?.data?.detail;
            const msg = formatApiErrorDetail(detail);
            if (status === 429 || /rate.?limit/i.test(msg)) {
                setError("Too many attempts from your network. Wait a few minutes (or switch to mobile data) and try again.");
            } else if (status === 400 && /token/i.test(msg)) {
                setError(msg);
            } else if (err.message === "Network Error") {
                setError("Couldn't reach the server. Check your internet and try again.");
            } else {
                setError(msg || "Reset failed. Try requesting a new link.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet><title>Reset password — OpenCSV</title></Helmet>
            <section className="min-h-[70vh] flex items-center">
                <div className="mx-auto max-w-md w-full px-4 md:px-8 py-16">
                    <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">/ recovery</div>
                    <h1 className="font-display font-black tracking-tighter text-4xl mb-6">Set a new password.</h1>
                    {!token ? (
                        <p className="text-sm text-signal-red">Missing reset token.</p>
                    ) : (
                        <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
                            <div>
                                <label className="ui-heading text-xs uppercase tracking-widest text-ink-soft">New password</label>
                                <input type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 w-full border border-ink/20 px-3 py-2.5 bg-white focus-cobalt" data-testid="reset-password-input" />
                            </div>
                            {error && (
                                <div className="border border-signal-red/40 bg-signal-red/5 p-3 text-sm" data-testid="reset-error">
                                    <div className="text-signal-red font-semibold">{error}</div>
                                    {/Invalid|used|expired|token/i.test(error) && (
                                        <p className="mt-2 text-xs text-ink-soft">
                                            Reset links expire after 1 hour and can only be used once.
                                            <Link to="/forgot-password" className="ml-1 text-cobalt underline">Request a fresh link →</Link>
                                        </p>
                                    )}
                                </div>
                            )}
                            <button type="submit" disabled={loading} className="btn-brutal w-full bg-cobalt text-white px-4 py-3 ui-heading font-semibold disabled:opacity-60" data-testid="reset-submit-button">
                                {loading ? "Saving…" : "Save new password"}
                            </button>
                            <p className="text-sm text-ink-soft mt-2">
                                <Link to="/login" className="text-cobalt hover:underline">Back to sign in</Link>
                            </p>
                        </form>
                    )}
                </div>
            </section>
        </>
    );
}
